<template>
    <pendo-card
        class="guide_details_card"
        :collapsible="true"
        body-min-height="0"
        title="Guide Details">
        <template #body>
            <div
                v-pendo-loading:material="loadingConfig"
                :class="`guide_details_card_container ${loading ? 'loading' : ''}`">
                <table v-if="!loading">
                    <thead>
                        <tr>
                            <th scope="col">
                                <div>
                                    <div>
                                        <template v-if="showPreviousRevision">
                                            <h2>Previous version</h2>
                                            <div role="doc-subtitle">
                                                {{ lastUpdateFormattedDateForPreviousRevision }} by
                                                {{ previousRevision.lastUpdatedByUser.username }}
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </th>
                            <th scope="col">
                                <div>
                                    <div>
                                        <h2>Selected version</h2>
                                        <div role="doc-subtitle">
                                            {{ lastUpdateFormattedDateForSelectedRevision }} by
                                            {{ selectedRevision.lastUpdatedByUser.username }}
                                        </div>
                                    </div>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(sectionData, sectionHeader) in revisionsData">
                            <tr :key="`${sectionHeader.replace(' ', '_')}_row_header`">
                                <th scope="row">
                                    <div>
                                        <div>
                                            <template v-if="showPreviousRevision">
                                                {{ sectionHeader }}
                                            </template>
                                        </div>
                                    </div>
                                </th>
                                <th scope="row">
                                    <div>
                                        <div>
                                            {{ sectionHeader }}
                                        </div>
                                    </div>
                                </th>
                            </tr>
                            <tr
                                v-for="(values, i) in sectionData"
                                :key="`${sectionHeader.replace(' ', '_')}_${i}`">
                                <td>
                                    <div>
                                        <div>
                                            <template v-if="showPreviousRevision">
                                                <span v-if="isBasicSection(sectionHeader)">{{ values.previous }}</span>
                                                <guide-status-changer
                                                    v-if="
                                                        sectionHeader === STATE_HEADER &&
                                                            values.previous &&
                                                            values.previous.label
                                                    "
                                                    :guide="previousRevision"
                                                    :disable-status-change="true"
                                                    :should-show-was-scheduled="true" />
                                                <guide-audit-localization-details
                                                    v-if="sectionHeader === LOCALIZATION_HEADER"
                                                    :languages="values.previous" />
                                                <div
                                                    v-if="sectionHeader === EMAIL_CONFIG_HEADER"
                                                    class="email-config-section">
                                                    <div>
                                                        Sender:
                                                        {{ formatValueForDisplay(values.previous, 'senderName') }}
                                                    </div>
                                                    <div>
                                                        Subject: {{ formatValueForDisplay(values.previous, 'subject') }}
                                                    </div>
                                                    <div>
                                                        Metadata property:
                                                        {{
                                                            formatValueForDisplay(
                                                                values.previous,
                                                                'emailMetadataPropertyName'
                                                            )
                                                        }}
                                                    </div>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div>
                                        <div>
                                            <span v-if="isBasicSection(sectionHeader)">{{ values.selected }}</span>
                                            <guide-status-changer
                                                v-if="sectionHeader === STATE_HEADER"
                                                :guide="selectedRevision"
                                                :disable-status-change="true"
                                                :should-show-was-scheduled="true" />
                                            <guide-audit-localization-details
                                                v-if="sectionHeader === LOCALIZATION_HEADER"
                                                :languages="values.selected" />
                                            <div
                                                v-if="sectionHeader === EMAIL_CONFIG_HEADER"
                                                class="email-config-section">
                                                <div>
                                                    Sender: {{ formatValueForDisplay(values.selected, 'senderName') }}
                                                </div>
                                                <div>
                                                    Subject: {{ formatValueForDisplay(values.selected, 'subject') }}
                                                </div>
                                                <div>
                                                    Metadata property:
                                                    {{
                                                        formatValueForDisplay(
                                                            values.selected,
                                                            'emailMetadataPropertyName'
                                                        )
                                                    }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </template>
    </pendo-card>
</template>

<script>
import GuideStatusChanger from '@/components/guides/common/GuideStatusChanger';
import GuideAuditLocalizationDetails from './GuideAuditLocalizationDetails.vue';
import { GUIDE_STATUS_COLORS } from '@/constants/guide-statuses';
import { PendoCard, PendoLoading } from '@pendo/components';
import { getSegmentName } from '@/utils/segments';
import {
    GUIDE_AUDIT_PROPS_TO_CHECK,
    getPropValue,
    formatRevisionDateTimestamp,
    formatSelectedLocalizations,
    formatRedisplay,
    formatFrequency,
    formatMobileDisplay
} from './guide-audit-utils';
import isEqual from 'lodash/isEqual';

export default {
    name: 'GuideAuditDetails',
    components: {
        GuideStatusChanger,
        PendoCard,
        GuideAuditLocalizationDetails
    },
    directives: {
        PendoLoading
    },
    props: {
        selectedRevision: {
            type: Object,
            default: () => ({})
        },
        previousRevision: {
            type: Object,
            default: () => ({})
        },
        activeTimezone: {
            type: String,
            required: true
        },
        segments: {
            type: Object,
            default: () => ({})
        },
        isMobileApp: {
            type: Boolean,
            default: false
        },
        languageMapping: {
            type: Object,
            default: () => ({})
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            propsToReview: Object.keys(GUIDE_AUDIT_PROPS_TO_CHECK),
            revisionsData: {},
            GUIDE_STATUS_COLORS,
            STATE_HEADER: this.getPropHeader(GUIDE_AUDIT_PROPS_TO_CHECK.STATE),
            LOCALIZATION_HEADER: this.getPropHeader(GUIDE_AUDIT_PROPS_TO_CHECK.LOCALIZATION),
            EMAIL_CONFIG_HEADER: this.getPropHeader(GUIDE_AUDIT_PROPS_TO_CHECK.EMAIL_CONFIG)
        };
    },
    computed: {
        lastUpdateFormattedDateForPreviousRevision () {
            return formatRevisionDateTimestamp(this.previousRevision.lastUpdatedAt, this.activeTimezone);
        },
        lastUpdateFormattedDateForSelectedRevision () {
            return formatRevisionDateTimestamp(this.selectedRevision.lastUpdatedAt, this.activeTimezone);
        },
        loadingConfig () {
            return {
                loading: this.loading,
                spinnerProps: {
                    color: '#3a3c45',
                    background: '#FFFFFF',
                    size: 32
                }
            };
        },
        showPreviousRevision () {
            return this.previousRevision && this.previousRevision.lastUpdatedByUser;
        }
    },
    watch: {
        selectedRevision () {
            this.parseRevisions();
        },
        previousRevision () {
            this.parseRevisions();
        }
    },
    mounted () {
        this.parseRevisions();
    },
    methods: {
        getSegmentName (segmentId) {
            return getSegmentName(segmentId, this.segments) || 'Everyone';
        },
        parseRevisions () {
            const values = Object.keys(GUIDE_AUDIT_PROPS_TO_CHECK).reduce((propMap, curr) => {
                const prop = GUIDE_AUDIT_PROPS_TO_CHECK[curr];
                const header = this.getPropHeader(prop);
                const values = this.getPropValue(prop);

                if (isEqual(values.selected, values.previous)) return propMap;
                if (!propMap[header]) propMap[header] = [];
                propMap[header].push(values);

                return propMap;
            }, {});

            this.revisionsData = values;
        },
        getPropValueFromRevision (revision, prop) {
            const { isMobileApp, activeTimezone, languageMapping } = this;

            return getPropValue(revision, prop, { isMobileApp, activeTimezone, languageMapping });
        },
        getPropHeader (prop) {
            switch (prop) {
                case GUIDE_AUDIT_PROPS_TO_CHECK.NAME:
                    return 'Guide name';
                case GUIDE_AUDIT_PROPS_TO_CHECK.LAUNCH_METHOD:
                    return 'Activation';
                case GUIDE_AUDIT_PROPS_TO_CHECK.REDISPLAY:
                    return 'Redisplay';
                case GUIDE_AUDIT_PROPS_TO_CHECK.DELIVERY_FREQUENCY:
                    return 'Delivery frequency';
                case GUIDE_AUDIT_PROPS_TO_CHECK.AUDIENCE:
                    return 'Segment';
                case GUIDE_AUDIT_PROPS_TO_CHECK.LOCALIZATION:
                    return 'Localization';
                case GUIDE_AUDIT_PROPS_TO_CHECK.SHOWS_AFTER:
                case GUIDE_AUDIT_PROPS_TO_CHECK.EXPIRES_AFTER:
                    return 'Scheduling';
                case GUIDE_AUDIT_PROPS_TO_CHECK.STATE:
                    return 'Status';
                case GUIDE_AUDIT_PROPS_TO_CHECK.DESCRIPTION:
                    return 'Description';
                case GUIDE_AUDIT_PROPS_TO_CHECK.DEVICE_TYPE:
                    return 'Show guide on';
                case GUIDE_AUDIT_PROPS_TO_CHECK.OVERRIDE_AUTO_THROTTLING:
                    return 'Throttle';
                case GUIDE_AUDIT_PROPS_TO_CHECK.EMAIL_CONFIG:
                    return 'Email configuration';
                case GUIDE_AUDIT_PROPS_TO_CHECK.MOBILE_DISPLAY:
                    return 'Mobile display settings';
            }
        },
        getPropValue (prop) {
            let selected = this.getPropValueFromRevision(this.selectedRevision, prop);
            let previous = this.getPropValueFromRevision(this.previousRevision, prop);

            switch (prop) {
                case GUIDE_AUDIT_PROPS_TO_CHECK.AUDIENCE:
                    previous = this.getSegmentName(previous);
                    selected = this.getSegmentName(selected);
                    break;
                case GUIDE_AUDIT_PROPS_TO_CHECK.LOCALIZATION:
                    selected = formatSelectedLocalizations(previous, selected);
                    previous = { all: previous };
                    break;
                case GUIDE_AUDIT_PROPS_TO_CHECK.REDISPLAY:
                    [previous, selected] = formatRedisplay(previous, selected);
                    break;
                case GUIDE_AUDIT_PROPS_TO_CHECK.DELIVERY_FREQUENCY:
                    [previous, selected] = formatFrequency(previous, selected);
                    break;
                case GUIDE_AUDIT_PROPS_TO_CHECK.MOBILE_DISPLAY:
                    [previous, selected] = formatMobileDisplay(previous, selected);
                    break;
            }

            return {
                previous,
                selected
            };
        },
        formatValueForDisplay (sectionData, key) {
            return sectionData[key] || '--';
        },
        isBasicSection (sectionHeader) {
            switch (sectionHeader) {
                case this.STATE_HEADER:
                case this.LOCALIZATION_HEADER:
                case this.EMAIL_CONFIG_HEADER:
                    return false;
                default:
                    return true;
            }
        }
    }
};
</script>
<style scoped lang="scss">
.email-config-section > * {
    margin-bottom: 5px;
}

.guide_details_card {
    flex: 3 1;

    .guide_details_card_container {
        padding-top: 16px;
        display: flex;

        &.loading {
            min-height: 401px;
        }

        table {
            table-layout: fixed;
            flex: 1;
            width: 100%;
            border-collapse: collapse;
            border-spacing: 0.75em;

            tbody {
                th {
                    font-weight: 600;
                    font-size: 14px;
                    padding-bottom: 8px;
                }

                tr {
                    td {
                        padding-bottom: 24px;
                    }
                }
            }

            tr > *:last-child {
                border-left: solid 1px $color-gray-30;
            }

            th:first-child {
                width: 50%;
            }

            thead {
                padding-bottom: 0.75em;

                h2 {
                    margin: 0px;
                    font-size: 16px;
                    font-weight: 600;
                }

                [role='doc-subtitle'] {
                    white-space: pre-line;
                    color: $color-gray-80;
                    font-weight: 100;
                    font-size: 12px;
                    margin-bottom: 24px;
                }
            }

            th,
            td {
                vertical-align: top;
                text-align: left;

                > div {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    flex-wrap: nowrap;
                    padding: 0px 64px;

                    > div {
                        flex: 1;
                        width: 100%;
                    }
                }
            }

            td {
                span {
                    white-space: pre-line;
                    line-height: 22px;
                }

                i {
                    display: inline-block;
                    padding-right: 5px;
                }
            }
        }
    }
}
</style>
